type ShipmentType = 'outbound' | 'inbound';
type BaseShipmentProps = {
  shipmentTranslationKey: string;
};

type GetStatusMessageProps = BaseShipmentProps & {
  status: string;
  type: ShipmentType;
};

type GetTranslatedStatusKeyProps = BaseShipmentProps & {
  statusKey: string;
};

type ShipmentStatusi18nKey =
  | 'LabKit.StatusCategory.Accepted'
  | 'LabKit.StatusCategory.Alert'
  | 'LabKit.StatusCategory.AvailableForPickup'
  | 'LabKit.StatusCategory.Delivered'
  | 'LabKit.StatusCategory.DeliveredToAgent'
  | 'LabKit.StatusCategory.DeliveryAttempt'
  | 'LabKit.StatusCategory.InTransitFromOriginProcessing'
  | 'LabKit.StatusCategory.MovingThroughNetwork'
  | 'LabKit.StatusCategory.OutForDelivery'
  | 'LabKit.StatusCategory.PreShipment'
  | 'LabKit.StatusCategory.PreparingForDelivery'
  | 'LabKit.StatusCategory.ProcessingAtDestination'
  | 'LabKit.StatusCategory.Unprocessed'
  | 'LabKit.NoStatus';

type ShipmentDescriptioni18nKey =
  | 'LabKit.ShippingStatus.USPSTrackingSiteWithSupport'
  | 'LabKit.ShippingStatus.Outbound.KitDelivered'
  | 'LabKit.ShippingStatus.Inbound.KitReceivedAtLab'
  | 'LabKit.ShippingStatus.Outbound.KitOnTheWay'
  | 'LabKit.ShippingStatus.Inbound.KitSentToLab';

export const COMPLETE_SHIPMENT_STATUSES = [
  'Delivered',
  'Delivered to Agent',
] as const;

const KNOWN_STATUSES = [
  'Accepted',
  'Alert',
  'Available for Pickup',
  'Delivered',
  'Delivered to Agent',
  'Delivery Attempt',
  'In Transit from Origin Processing',
  'Moving Through Network',
  'Out for Delivery',
  'Pre-Shipment',
  'Preparing for Delivery',
  'Processing at Destination',
  'Unprocessed',
] as const;

const isKnownStatus = ({
  status,
}: {
  status: (typeof KNOWN_STATUSES)[number];
}) => {
  return KNOWN_STATUSES.includes(status);
};

export const getTranslatedStatusKey = ({
  statusKey,
  shipmentTranslationKey,
}: GetTranslatedStatusKeyProps): ShipmentStatusi18nKey => {
  const statusMappings: Record<string, ShipmentStatusi18nKey> = {
    Accepted:
      `${shipmentTranslationKey}.StatusCategory.Accepted` as ShipmentStatusi18nKey,
    Alert:
      `${shipmentTranslationKey}.StatusCategory.Alert` as ShipmentStatusi18nKey,
    'Available for Pickup':
      `${shipmentTranslationKey}.StatusCategory.AvailableForPickup` as ShipmentStatusi18nKey,
    Delivered:
      `${shipmentTranslationKey}.StatusCategory.Delivered` as ShipmentStatusi18nKey,
    'Delivered to Agent':
      `${shipmentTranslationKey}.StatusCategory.DeliveredToAgent` as ShipmentStatusi18nKey,
    'Delivery Attempt':
      `${shipmentTranslationKey}.StatusCategory.DeliveryAttempt` as ShipmentStatusi18nKey,
    'In Transit from Origin Processing':
      `${shipmentTranslationKey}.StatusCategory.InTransitFromOriginProcessing` as ShipmentStatusi18nKey,
    'Moving Through Network':
      `${shipmentTranslationKey}.StatusCategory.MovingThroughNetwork` as ShipmentStatusi18nKey,
    'Out for Delivery':
      `${shipmentTranslationKey}.StatusCategory.OutForDelivery` as ShipmentStatusi18nKey,
    'Pre-Shipment':
      `${shipmentTranslationKey}.StatusCategory.PreShipment` as ShipmentStatusi18nKey,
    'Preparing for Delivery':
      `${shipmentTranslationKey}.StatusCategory.PreparingForDelivery` as ShipmentStatusi18nKey,
    'Processing at Destination':
      `${shipmentTranslationKey}.StatusCategory.ProcessingAtDestination` as ShipmentStatusi18nKey,
    Unprocessed:
      `${shipmentTranslationKey}.StatusCategory.Unprocessed` as ShipmentStatusi18nKey,
  };

  if (statusMappings[statusKey]) {
    return statusMappings[statusKey];
  }

  return `${shipmentTranslationKey}.NoStatus` as ShipmentStatusi18nKey;
};

export const getStatusMessage = ({
  status,
  type,
  shipmentTranslationKey,
}: GetStatusMessageProps): ShipmentDescriptioni18nKey => {
  const uspsTrackingSiteWithSupport =
    `${shipmentTranslationKey}.ShippingStatus.USPSTrackingSiteWithSupport` as ShipmentDescriptioni18nKey;
  const kitDelivered =
    `${shipmentTranslationKey}.ShippingStatus.Outbound.KitDelivered` as ShipmentDescriptioni18nKey;
  const kitReceivedAtLab =
    `${shipmentTranslationKey}.ShippingStatus.Inbound.KitReceivedAtLab` as ShipmentDescriptioni18nKey;
  const kitOnTheWay =
    `${shipmentTranslationKey}.ShippingStatus.Outbound.KitOnTheWay` as ShipmentDescriptioni18nKey;
  const kitSentToLab =
    `${shipmentTranslationKey}.ShippingStatus.Inbound.KitSentToLab` as ShipmentDescriptioni18nKey;

  if (status === '') return uspsTrackingSiteWithSupport;

  if (!isKnownStatus({ status: status as (typeof KNOWN_STATUSES)[number] })) {
    return uspsTrackingSiteWithSupport;
  }

  const messages: Record<
    string,
    {
      outbound: ShipmentDescriptioni18nKey;
      inbound: ShipmentDescriptioni18nKey;
    }
  > = {
    Accepted: {
      outbound: uspsTrackingSiteWithSupport,
      inbound: uspsTrackingSiteWithSupport,
    },
    Alert: {
      outbound: uspsTrackingSiteWithSupport,
      inbound: uspsTrackingSiteWithSupport,
    },
    'Available for Pickup': {
      outbound: uspsTrackingSiteWithSupport,
      inbound: uspsTrackingSiteWithSupport,
    },
    'Delivery Attempt': {
      outbound: uspsTrackingSiteWithSupport,
      inbound: uspsTrackingSiteWithSupport,
    },
    'Processing at Destination': {
      outbound: uspsTrackingSiteWithSupport,
      inbound: uspsTrackingSiteWithSupport,
    },
    Unprocessed: {
      outbound: uspsTrackingSiteWithSupport,
      inbound: uspsTrackingSiteWithSupport,
    },
    Delivered: {
      outbound: kitDelivered,
      inbound: kitReceivedAtLab,
    },
    'Delivered to Agent': {
      outbound: kitOnTheWay,
      inbound: kitReceivedAtLab,
    },
    'In Transit from Origin Processing': {
      outbound: kitOnTheWay,
      inbound: kitSentToLab,
    },
    'Moving Through Network': {
      outbound: kitOnTheWay,
      inbound: kitSentToLab,
    },
    'Out for Delivery': {
      outbound: kitOnTheWay,
      inbound: kitSentToLab,
    },
    'Pre-Shipment': {
      outbound: kitOnTheWay,
      inbound: kitSentToLab,
    },
    'Preparing for Delivery': {
      outbound: kitOnTheWay,
      inbound: kitSentToLab,
    },
  };

  const message = messages[status]?.[type];
  return message || uspsTrackingSiteWithSupport;
};

export const shouldShowSupportLink = (status: string): boolean => {
  if (!status || status === '') {
    return true;
  }

  if (!isKnownStatus({ status: status as (typeof KNOWN_STATUSES)[number] })) {
    return true;
  }

  return [
    'Accepted',
    'Alert',
    'Available for Pickup',
    'Delivery Attempt',
    'Processing at Destination',
    'Unprocessed',
  ].includes(status);
};
